import { zodResolver } from '@hookform/resolvers/zod';
import { CloseIcon } from 'components/icons';
import PlacesAutocomplete from 'components/PlaceSearchBox';
import { defaultApiErrorSchema } from 'lib/api/utils';
import { useEnableWifiCalling } from 'lib/hooks/sim/sim.mutations';
import {
  useSimCardDetail,
  useWifiCallingData,
} from 'lib/hooks/sim/sim.queries';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { SimType } from 'types/shared';
import { z } from 'zod';

/**
 * This regular expression is designed to validate US street addresses.
 *
 * * `(\d{1,5}\s)?` - Optional 1-5 digits followed by a space
 * * `([A-Za-z\s\d.#\-',]*)` - A combination of letters, numbers, spaces, periods, hash symbols, hyphens, apostrophes, and commas
 *
 * Examples of valid street addresses:
 * - 123 Main Street
 * - 42nd Street
 * - Broadway
 * - 5th Ave.
 * - 10 Downing St.
 * - 221B Baker Street
 * - O'Connor Street
 * - Martin Luther King Jr. Blvd.
 */
const streetRegex = /^(\d{1,5}\s)?([A-Za-z\s\d.#\-',]*)$/;

const e911AddressFormSchema = z.object({
  addressLine1: z
    .string()
    .min(1, 'Address Line 1 is required')
    .regex(streetRegex, 'Please enter a valid street address'),
  addressLine2: z
    .string()
    .regex(streetRegex, 'Please enter a valid street address')
    .optional(),
  city: z
    .string()
    .min(1, 'City is required')
    .regex(
      /^[A-Za-z\s\-'.]+$/,
      'City name should contain english alphabets or hyphens only'
    ),
  state: z
    .string()
    .min(1, 'State is required')
    .regex(
      /^[A-Za-z\s\-'.]+$/,
      'State should contain english alphabets or hyphens only'
    ),
  zipcode: z
    .string()
    .min(1, 'Zipcode is required')
    .length(5, 'Zipcode must be 5 digits')
    .regex(/^\d+$/, 'Zipcode must contain digits only'),
});

type E911AddressFormSchema = z.infer<typeof e911AddressFormSchema>;

type E911AddressModalProps = {
  iccid: string;
  isOpen: boolean;
  onClose: () => void;
};

function E911AddressModal({ iccid, isOpen, onClose }: E911AddressModalProps) {
  const { data: wifiCallingDetails } = useWifiCallingData({ iccid });
  const { data: simDetail } = useSimCardDetail({ iccid });
  const enableWifiCallingMutation = useEnableWifiCalling();
  const [isThankYouDialogOpen, setIsThankYouDialogOpen] = useState(false);

  const { setFocus, formState, setValue, ...form } =
    useForm<E911AddressFormSchema>({
      defaultValues: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipcode: '',
      },
      resolver: zodResolver(e911AddressFormSchema),
      reValidateMode: 'onChange',
      mode: 'all', // will validate on blur, change and submit events
    });

  useEffect(() => {
    if (!wifiCallingDetails?.enabled) return;

    const { street1, street2, state, city, zip } =
      wifiCallingDetails.e911Address ?? {};

    setValue('addressLine1', street1 ?? '');
    setValue('addressLine2', street2 ?? '');
    setValue('city', city ?? '');
    setValue('state', state ?? '');
    setValue('zipcode', zip ?? '');
  }, [setValue, wifiCallingDetails]);

  const closeModalHandler = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (values: E911AddressFormSchema) => {
    if (!simDetail) return;

    const { addressLine1, addressLine2, city, state, zipcode } = values;

    try {
      await enableWifiCallingMutation.mutateAsync({
        simId: simDetail.id,
        simNumber: simDetail.simnumber,
        simtype: simDetail.simtype as SimType,
        addressLine1,
        addressLine2,
        city,
        state,
        zipcode,
      });

      setIsThankYouDialogOpen(true);
    } catch (error) {
      const apiErrorCode = defaultApiErrorSchema
        .merge(z.object({ data: z.object({ error: z.string() }) }))
        .safeParse(error).data?.data.error;
      const apiErrorMessage = defaultApiErrorSchema
        .merge(z.object({ data: z.object({ message: z.string() }) }))
        .safeParse(error).data?.data.message;
      const errorMessage =
        apiErrorCode === 'INVALID_E911_ADDRESS'
          ? 'The provided e911 address is invalid.'
          : (apiErrorMessage ??
            'Something went wrong when updating e911 address');

      form.setError('root', {
        message: errorMessage,
      });
    }
  };

  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={closeModalHandler}
        ariaHideApp={false}
        contentLabel="Modify e911 Address"
        portalClassName="react-modal"
        overlayClassName="modal"
        className="modal-dialog react-modal-dialog-sm modal-dialog-centered"
        style={{
          overlay: { display: isThankYouDialogOpen ? 'none' : 'block' },
        }}
      >
        <div className="modal-content" hidden={isThankYouDialogOpen}>
          <div className="react-modal-header">
            <h5 className="react-modal-title">e911 Address</h5>
            <button
              type="button"
              className="btn react-modal-close"
              onClick={closeModalHandler}
            >
              <CloseIcon className={''} />
            </button>
          </div>

          <div className="react-modal-body">
            <form
              onSubmit={form.handleSubmit(onSubmit, (e) => {
                // Using setTimeout to ensure addressLine1 gets focus after react-hook-form's autofocus
                // This is necessary because react-hook-form prioritizes native inputs over Controller inputs
                setTimeout(() => {
                  if (e.addressLine1) setFocus('addressLine1');
                }, 1);
              })}
              noValidate
            >
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group material-textfield">
                      <Controller
                        name="addressLine1"
                        control={form.control}
                        render={({ field }) => (
                          <PlacesAutocomplete
                            type="text"
                            {...field}
                            ref={null}
                            textInputRef={field.ref}
                            className="form-control form-control-lg material-textfield-input"
                            handleAddressChange={({
                              address,
                              city,
                              state,
                              zip,
                            }: any) => {
                              setValue('addressLine1', address, {
                                shouldValidate: true,
                              });
                              setValue('city', city, {
                                shouldValidate: true,
                              });
                              setValue('state', state, {
                                shouldValidate: true,
                              });
                              setValue('zipcode', zip, {
                                shouldValidate: true,
                              });
                            }}
                            address={field.value}
                            setValue={(address: string) => {
                              setValue('addressLine1', address, {
                                shouldValidate: true,
                              });
                            }}
                            isRequired={true}
                          />
                        )}
                      />

                      {formState.errors.addressLine1 && (
                        <span className="invalid-feedback d-block">
                          {formState.errors.addressLine1.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group material-textfield">
                      <input
                        {...form.register('addressLine2')}
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        id="address-line-2"
                        required
                      />

                      <label
                        className="material-textfield-label"
                        htmlFor="address-line-2"
                      >
                        Address Line 2
                      </label>

                      {formState.errors.addressLine2 && (
                        <span className="invalid-feedback d-block">
                          {formState.errors.addressLine2.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group material-textfield">
                      <input
                        {...form.register('city')}
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        id="city"
                        required
                      />

                      <label
                        className="material-textfield-label"
                        htmlFor="city"
                      >
                        City <span className="text-danger">*</span>
                      </label>

                      {formState.errors.city && (
                        <span className="invalid-feedback d-block">
                          {formState.errors.city.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group material-textfield">
                      <input
                        {...form.register('state')}
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        required
                        id="state"
                      />

                      <label
                        className="material-textfield-label"
                        htmlFor="state"
                      >
                        State <span className="text-danger">*</span>
                      </label>

                      {formState.errors.state && (
                        <span className="invalid-feedback d-block">
                          {formState.errors.state.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group material-textfield">
                      <input
                        {...form.register('zipcode')}
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        required
                        onChange={(e) => {
                          form.setError('root', {
                            message: '',
                          });
                          form.register('zipcode').onChange(e);
                        }}
                        id="zipcode"
                      />

                      <label
                        className="material-textfield-label"
                        htmlFor="zipcode"
                      >
                        Zip <span className="text-danger">*</span>
                      </label>

                      {formState.errors.zipcode && (
                        <span className="invalid-feedback d-block">
                          {formState.errors.zipcode.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {formState.errors.root && (
                  <span className="invalid-feedback d-block">
                    {formState.errors.root.message}
                  </span>
                )}
              </div>

              <button
                type="submit"
                disabled={formState.isSubmitting}
                className="btn btn-dark btn-lg w-100"
              >
                {formState.isSubmitting ? 'Please wait...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </ReactModal>

      <ThankYouDialog
        isOpen={isThankYouDialogOpen}
        onClose={() => {
          setIsThankYouDialogOpen(false);
          closeModalHandler();
        }}
      />
    </>
  );
}

export default E911AddressModal;

type ThankYouDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function ThankYouDialog({ isOpen, onClose }: ThankYouDialogProps) {
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      contentLabel="Thank You"
      portalClassName="react-modal"
      overlayClassName="modal"
      className="modal-dialog react-modal-dialog-sm modal-dialog-centered"
    >
      <div className="modal-content">
        <div className="react-modal-header">
          <h5 className="react-modal-title">Thank You</h5>
          <button
            type="button"
            className="btn react-modal-close"
            onClick={onClose}
          >
            <CloseIcon className={''} />
          </button>
        </div>

        <div className="react-modal-body">
          <p className="react-modal-subtitle">
            We have submitted your request and e911 address will be updated
            momentarily.
          </p>

          <div className="text-right mt-4">
            <button onClick={onClose} className="btn btn-dark w-150">
              OK
            </button>
          </div>
        </div>
      </div>
    </ReactModal>
  );
}
